import React, { useState, useEffect, useCallback } from 'react';
import ProductFields from './ProductFields';
import ScoreSlider from './ScoreSlider';
import OfficialBadge from './OfficialBadge';
import AttachmentPicker from './AttachmentPicker';
import { useI18n } from './i18n';
import {
  PRODUCT_INITIAL, productPayload, OIL_DEFECTS, OIL_OTHER_DEFECTS, OIL_POSITIVES, oilResult,
} from './productScoring';

const DRAFT_KEY = 'tnh2_draft_oil';

const INITIAL = {
  ...PRODUCT_INITIAL,
  sample_code: '', serving_temperature: 28,
  tasting_date: new Date().toISOString().slice(0, 10), venue: '', taster: '',
  ...Object.fromEntries(OIL_DEFECTS.map((k) => [k, 0])),
  defect_other_name: '', defect_other_intensity: 0,
  fruity: 0, fruity_type: '', bitter: 0, pungent: 0,
  notes: '', visibility: 'private',
};

function loadDraft() {
  try {
    const saved = localStorage.getItem(DRAFT_KEY);
    if (saved) return { ...INITIAL, ...JSON.parse(saved) };
  } catch { /* ignore */ }
  return INITIAL;
}

function fromSheet(sheet) {
  const out = { ...INITIAL };
  const product = sheet.product || {};
  out.product_name = sheet.product_name || product.name || '';
  ['producer', 'origin', 'variety'].forEach((k) => { out[k] = sheet[k] || product[k] || ''; });
  out.harvest_year = sheet.harvest_year || product.harvest_year || '';
  Object.keys(INITIAL).forEach((k) => {
    if (k in PRODUCT_INITIAL) return;
    if (sheet[k] !== undefined && sheet[k] !== null) out[k] = sheet[k];
  });
  return out;
}

// IOC/COI profile sheet of one taster. Props: onSubmit(data, attachmentItems), initialData, onCancel.
function OilForm({ onSubmit, initialData, onCancel }) {
  const { t, tc, formatNumber } = useI18n();
  const editing = !!initialData;
  const [form, setForm] = useState(() => (initialData ? fromSheet(initialData) : loadDraft()));
  const [attachmentItems, setAttachmentItems] = useState([]);

  useEffect(() => { if (!editing) localStorage.setItem(DRAFT_KEY, JSON.stringify(form)); }, [form, editing]);

  const patch = useCallback((changes) => setForm((prev) => ({ ...prev, ...changes })), []);
  const handleInput = (e) => patch({ [e.target.name]: e.target.value });
  const setValue = useCallback((name, value) => patch({ [name]: value }), [patch]);

  const result = oilResult(form);

  // The "other defect" box shows translated suggestions but stores the code of a suggestion, or the free text
  const otherLabel = (value) => tc('oil.other_defect', value);
  const handleOtherName = (e) => {
    const typed = e.target.value;
    const match = OIL_OTHER_DEFECTS.find((code) => t(`oil.other_defect.${code}`).toLowerCase() === typed.trim().toLowerCase());
    patch({ defect_other_name: match || typed });
  };

  const handleReset = () => {
    if (!window.confirm(t('oil.confirm_reset'))) return;
    setForm(INITIAL);
    setAttachmentItems([]);
    localStorage.removeItem(DRAFT_KEY);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.product_name.trim()) { alert(t('oil.alert_name')); return; }
    const text = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
    const temperature = parseFloat(form.serving_temperature);
    const data = {
      ...productPayload(form),
      sample_code: text(form.sample_code), serving_temperature: Number.isNaN(temperature) ? null : temperature,
      tasting_date: form.tasting_date || null, venue: text(form.venue), taster: text(form.taster), notes: text(form.notes),
      visibility: form.visibility,
      ...Object.fromEntries(OIL_DEFECTS.map((k) => [k, Number(form[k]) || 0])),
      defect_other_name: text(form.defect_other_name), defect_other_intensity: Number(form.defect_other_intensity) || 0,
      fruity: Number(form.fruity) || 0, fruity_type: form.fruity_type || null,
      bitter: Number(form.bitter) || 0, pungent: Number(form.pungent) || 0,
    };
    onSubmit(data, attachmentItems);
    if (editing) return;
    setForm(INITIAL);
    setAttachmentItems([]);
    localStorage.removeItem(DRAFT_KEY);
  };

  const slider = (name, extra = {}) => (
    <ScoreSlider key={name} label={t(`oil.p.${name}`)} hint={t(`oil.def.${name}`)} value={form[name]}
      min={0} max={10} step={0.1} nullable={false} onChange={(v) => setValue(name, v)} {...extra} />
  );

  return (
    <form className="sheet-form" onSubmit={handleSubmit}>
      <OfficialBadge status="formal" source={t('products.source.oil')} note={t('oil.badge_note')} sourceNote={t('products.source_note.oil')} />

      <fieldset>
        <legend>{t('oil.sec.product')}</legend>
        <div className="form-grid">
          <ProductFields category="oil" values={form} onChange={patch} />
          <div className="form-field">
            <label>{t('oil.f.sample_code')}</label>
            <input name="sample_code" value={form.sample_code} onChange={handleInput} maxLength={50} />
          </div>
          <div className="form-field">
            <label>{t('oil.f.serving_temperature')}</label>
            <input name="serving_temperature" type="number" step={0.5} min={0} max={60} value={form.serving_temperature} onChange={handleInput} />
          </div>
          <div className="form-field">
            <label>{t('oil.f.tasting_date')}</label>
            <input name="tasting_date" type="date" value={form.tasting_date} onChange={handleInput} />
          </div>
          <div className="form-field">
            <label>{t('oil.f.venue')}</label>
            <input name="venue" value={form.venue} onChange={handleInput} />
          </div>
          <div className="form-field">
            <label>{t('oil.f.taster')}</label>
            <input name="taster" value={form.taster} onChange={handleInput} />
          </div>
        </div>
      </fieldset>

      <fieldset>
        <legend>{t('oil.sec.defects')}</legend>
        <small className="import-hint">{t('oil.scale_hint')}</small>
        {OIL_DEFECTS.map((name) => slider(name))}
        <div className="form-field">
          <label>{t('oil.f.defect_other_name')}</label>
          <input list="oil-other-defects" value={form.defect_other_name ? otherLabel(form.defect_other_name) : ''}
            onChange={handleOtherName} maxLength={100} placeholder={t('oil.ph.defect_other')} />
          <datalist id="oil-other-defects">
            {OIL_OTHER_DEFECTS.map((code) => <option key={code} value={t(`oil.other_defect.${code}`)} />)}
          </datalist>
        </div>
        {slider('defect_other_intensity')}
      </fieldset>

      <fieldset>
        <legend>{t('oil.sec.positives')}</legend>
        {slider('fruity')}
        <div className="form-field">
          <label>{t('oil.f.fruity_type')}</label>
          <div className="chip-group">
            {['green', 'ripe'].map((type) => (
              <button type="button" key={type} className={`chip ${form.fruity_type === type ? 'selected' : ''}`}
                onClick={() => patch({ fruity_type: form.fruity_type === type ? '' : type })}>{tc('oil.fruity_type', type)}</button>
            ))}
          </div>
        </div>
        {OIL_POSITIVES.filter((n) => n !== 'fruity').map((name) => slider(name))}
      </fieldset>

      <fieldset>
        <legend>{t('oil.sec.result')}</legend>
        <div className="live-result">
          <span className="live-total">{tc('oil.category', result.categoryEu)}</span>
          <span>{t('oil.res.max_defect')}: <strong>{formatNumber(result.maxDefect, 1)}</strong></span>
          <span>{t('oil.res.ioc')}: <strong>{tc('oil.category', result.categoryIoc)}</strong></span>
          {OIL_POSITIVES.map((name) => (
            <span key={name}>{t(`oil.p.${name}`)}: <strong>{tc('oil.band', result.bands[name])}</strong></span>
          ))}
          {result.mild && <span className="chip selected">{t('oil.res.mild')}</span>}
          {result.wellBalanced && <span className="chip selected">{t('oil.res.well_balanced')}</span>}
          <small>{t('oil.res.indicative')}</small>
        </div>
        <div className="form-field">
          <label>{t('oil.f.notes')}</label>
          <textarea name="notes" value={form.notes} onChange={handleInput} rows={3} placeholder={t('oil.ph.notes')} />
        </div>
      </fieldset>

      {!editing && (
        <fieldset>
          <legend>{t('attachments.title')}</legend>
          <AttachmentPicker items={attachmentItems} onChange={setAttachmentItems} />
        </fieldset>
      )}

      <div className="form-field publish-opt">
        <label className="publish-label">
          <input type="checkbox" checked={form.visibility === 'public'}
            onChange={(e) => setValue('visibility', e.target.checked ? 'public' : 'private')} />
          {' '}{t('ui.publish_label')}
        </label>
        <small className="import-hint">{t('ui.publish_hint')}</small>
      </div>

      <div className="form-buttons">
        <button type="submit" className="submit-btn">{editing ? t('oil.save_edit') : t('oil.save_new')}</button>
        {editing
          ? <button type="button" className="reset-btn" onClick={onCancel}>{t('ui.cancel')}</button>
          : <button type="button" className="reset-btn" onClick={handleReset}>{t('ui.reset')}</button>}
      </div>
    </form>
  );
}

export default OilForm;
